/**
 * A drawn label as the 1-bit image thermal printers take: one bit per dot, a
 * set bit is a black dot, rows packed most-significant bit first.
 */
export interface MonoBitmap {
  widthBytes: number;
  height: number;
  bits: Uint8Array<ArrayBuffer>;
}

/** RGBA pixels, as a canvas's `getImageData()` returns them. */
export interface RgbaImage {
  data: Uint8Array | Uint8ClampedArray;
  width: number;
  height: number;
}

/** Anything darker than mid-grey (r + g + b below this) prints as a dot. */
export const DARK_THRESHOLD = 384;

export function toMonoBitmap(image: RgbaImage): MonoBitmap {
  const { data, width, height } = image;
  if (width % 8 !== 0) throw new Error(`label width must be a multiple of 8 dots; got ${width}`);
  if (data.length !== width * height * 4) throw new Error("pixel data does not match the image size");
  const widthBytes = width / 8;
  const bits = new Uint8Array(widthBytes * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (data[i] + data[i + 1] + data[i + 2] < DARK_THRESHOLD) bits[y * widthBytes + (x >> 3)] |= 0x80 >> (x & 7);
    }
  }
  return { widthBytes, height, bits };
}
